import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@os/components/ui/button';
import { Card } from '@os/components/ui/card';
import { Input } from '@os/components/ui/input';
import {
  Eye,
  Maximize2,
  Minimize2,
  Code2,
  Copy as CopyIcon,
  Clipboard as PasteIcon,
  Eraser,
  Undo2,
  Redo2,
  Rows,
  Columns,
  Merge,
  Split,
  Trash2,
  // ADD: lock icon
  Lock
} from 'lucide-react';

import { supabase } from '@os/integrations/supabase/client';
import { useToast } from '@os/components/ui/use-toast';
import { ToastAction } from '@os/components/ui/toast';
import { saveAs } from 'file-saver';
import { useAuth } from '@os/hooks/useAuth';
import { DraggableFileDialog } from '@os/components/DraggableFileDialog';
import { ensureDir } from '@os/utils/filesystem';
import { useIsMobile } from '@os/hooks/use-mobile';
import MapleWordRibbonToggle from './MapleWordRibbonToggle';
import { getDocumentsPath } from '@/lib/osVFSBridge';
import MapleWordLinkProjectDialog from './MapleWordLinkProjectDialog';

// TipTap
import { EditorContent, useEditor, Extension } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import UnderlineExtension from '@tiptap/extension-underline';
import { TextSelection } from '@tiptap/pm/state';
// Replaced default Image with a resizable node view
import ResizableImage from './extensions/ResizableImage';
import { asBlob } from 'html-docx-js-typescript';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlignExt from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableHeader } from '@tiptap/extension-table-header';
import { TableCell } from '@tiptap/extension-table-cell';
import FontFamily from '@tiptap/extension-font-family';
import FontSize from '@tiptap/extension-font-size';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import CharacterCount from '@tiptap/extension-character-count';
import Typography from '@tiptap/extension-typography';
import ChangeMark from './extensions/ChangeMark';

// UI pieces
import MapleWordMenuBar from './MapleWordMenuBar';
import MapleWordAiMenu from './MapleWordAiMenu';
import MapleWordHeaderActions from './MapleWordHeaderActions';
import './MapleWord.styles.css';
import { ContextMenu, useContextMenu, ContextMenuItem } from '@os/components/ui/context-menu-custom';
import MapleWordStatusBar from './MapleWordStatusBar';
import MapleWordInfoPane, { MapleWordContextInfo } from './MapleWordInfoPane';
import { SidebarOpen, SidebarClose } from 'lucide-react';
import MapleWordReviewPanel from './MapleWordReviewPanel';

// ADD: markdown converter
import { mdToHtml } from '../../utils/markdown';
// ADD: load from OS VFS if initialContent is missing
import { globalVFS } from "@os/utils/storage/VFS";
import { globalNavigator } from "../WindowContent";

// ADD: simple content type helpers
const isLikelyHtml = (value: string) => {
  const v = (value || "").trim();
  // Heuristic: any angle-bracketed tags indicate HTML
  return /<\/?[a-z][\s\S]*>/i.test(v);
};

const isLikelyMarkdown = (value: string) => {
  const v = (value || "").trim();
  // Heuristics for common MD patterns: headings, lists, links, code fences
  return (
    /^#{1,6}\s/.test(v) || // # Heading
    /^(\*|-|\+)\s/.test(v) || // list items
    /\[[^\]]+\]\([^)]+\)/.test(v) || // links
    /```[\s\S]*```/.test(v) || // code fences
    /^>\s/.test(v) // blockquote
  );
};

const hasBlockHtml = (value: string) => {
  return /<(h[1-6]|ul|ol|pre|code|table|blockquote|img|figure|section|article)\b/i.test((value || "").trim());
};

const stripOuterPTags = (value: string) => {
  return (value || "")
    .trim()
    .replace(/^<p>/i, '')
    .replace(/<\/p>$/i, '')
    .replace(/<\/p>\s*<p>/gi, '\n\n')     // paragraph joins -> newlines
    .replace(/<br\s*\/?>/gi, '\n');       // line breaks -> newlines
};

const stripAllTags = (value: string) => {
  return (value || "").replace(/<\/?[^>]+>/g, '');
};

// Normalize "inline markdown in one line" into real Markdown (newlines before headings/lists/labels)
const normalizeHybridMarkdown = (value: string) => {
  let md = (value || "").trim();

  // Insert blank lines before headings found mid-line: " ... ### Heading" -> "\n\n### Heading"
  md = md.replace(/\s(#{1,6})\s/g, '\n\n$1 ');

  // Numbered lists: " 1. " mid-line -> "\n1. "
  md = md.replace(/\s(\d+)\.\s/g, '\n$1. ');

  // Bullets: " - " mid-line -> "\n- "
  md = md.replace(/\s-\s/g, '\n- ');

  // Speaker labels: " You: " / " AI: " mid-line -> "\n\nYou: " / "\n\nAI: "
  md = md.replace(/\s(You:|AI:)\s/g, '\n\n$1 ');

  return md.trim();
};

// Convert any incoming raw text into HTML suitable for TipTap
const toDisplayHtml = async (raw: string): Promise<string> => {
  const input = (raw || '').trim();
  if (!input) return '<p></p>';

  // If it's not HTML, treat as Markdown
  if (!isLikelyHtml(input)) {
    return await mdToHtml(input);
  }

  // If it's HTML but only a single paragraph and contains Markdown cues, unwrap and render as Markdown
  const singleP = /^<p>[\s\S]*<\/p>$/.test(input) && !hasBlockHtml(input);
  const containsMd = isLikelyMarkdown(input) || /#{1,6}\s|(\d+)\.\s|-\s/.test(input);

  if (singleP && containsMd) {
    const unwrapped = stripOuterPTags(input);
    const withoutTags = stripAllTags(unwrapped);
    const normalized = normalizeHybridMarkdown(withoutTags);
    return await mdToHtml(normalized);
  }

  // Otherwise, render as-is (valid HTML content)
  return input;
};

interface MapleWordProps {
  filePath?: string;
  initialContent?: string;
  onOpenWindow?: (appId: string, title: string, filePath?: string, content?: string) => void;
  // NEW: incoming UUIDs from MapleAccess
  chatId?: string;
  projectId?: string;
}

const MW_LOG_PREFIX = '[MapleWord]';
const mwLog = (...args: any[]) => console.log(MW_LOG_PREFIX, ...args);

export const MapleWord: React.FC<MapleWordProps> = ({ filePath, initialContent, onOpenWindow, chatId, projectId }) => {
  // Core state
  const [documentTitle, setDocumentTitle] = useState('Untitled Document');
  const [currentFilePath, setCurrentFilePath] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [wordCount, setWordCount] = useState(0);
  const [characterCount, setCharacterCount] = useState(0);
  const [showFileBrowser, setShowFileBrowser] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveFileName, setSaveFileName] = useState('');
  const [htmlContent, setHtmlContent] = useState<string>(initialContent || '');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showCode, setShowCode] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [osUsername, setOsUsername] = useState<string>('User');
  const [usernameReady, setUsernameReady] = useState<boolean>(false);
  const [showInfoPane, setShowInfoPane] = useState(false);
  const [infoContext, setInfoContext] = useState<MapleWordContextInfo>({
    source: undefined,
    projectName: undefined,
    chatTitle: undefined,
    knowledgeBaseFolder: '—',
    modelsInfo: 'Based on Subscription Plan',
    trustEnabled: false,
    osAccessEnabled: false,
    modelsUsed: [],
    chatId: undefined,
    projectId: undefined,
  });
  const [isLocked, setIsLocked] = useState<boolean>(false);
  // NEW: linking state and modal
  const [linkedProjectId, setLinkedProjectId] = useState<string | null>(null);
  const [showLinkDialog, setShowLinkDialog] = useState<boolean>(false);

  // Replace the constant reviewMode with a real toggleable state
  const [reviewMode, setReviewMode] = useState<boolean>(false);

  const [pendingRevision, setPendingRevision] = useState<{
    proposedHtml: string;
    proposedText: string;
    proposedTitle?: string;
    replace: boolean;
  } | null>(null);

  const [showReviewPane, setShowReviewPane] = useState<boolean>(false);
  const [changes, setChanges] = useState<Array<{ id: string; type: 'insert' | 'delete'; text: string; from: number; to: number; at: string }>>([]);
  const lastTextRef = useRef<string>('');

  // Guard to prevent recursive tracking when we apply marks
  const isApplyingMarkRef = useRef<boolean>(false);
  // Skip first update(s) after initial load to avoid marking the whole doc
  const hasInitializedRef = useRef<boolean>(false);
  const baselineTextRef = useRef<string>('');
  const pendingInsertRef = useRef<{ startIdx: number; pmFrom: number; text: string; lastAt: number } | null>(null);
  const idleTimerRef = useRef<number | null>(null);
  // NEW: Track when AI is streaming to batch all tokens into a single change
  const isAIStreamingRef = useRef<boolean>(false);
  const aiStreamStartPosRef = useRef<number | null>(null);

  const isMobile = useIsMobile();
  const [ribbonCollapsed, setRibbonCollapsed] = useState(false);

  const { toast } = useToast();
  const { user } = useAuth();

  const { contextMenu, showContextMenu, hideContextMenu } = useContextMenu();

  // Title input ref to manage focus/blur
  const titleInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Helper: derive folder path from filePath
  const getFolderFromFilePath = (path?: string) => {
    if (!path) return '';
    const idx = path.lastIndexOf('\\');
    return idx > 0 ? path.slice(0, idx) : '';
  };

  // NEW: resolve info pane data using Supabase + VFS markers
  useEffect(() => {
    const resolveContext = async () => {
      const folder = getFolderFromFilePath(filePath);
      if (!folder) return;

      let meta: any = null;
      try {
        await globalNavigator.waitForInitialization();
        await globalNavigator.syncWithVFS();
        const rawMeta = await (await import('@os/utils/storage/VFS')).globalVFS.load(`${folder}\\meta.json`);
        if (rawMeta) meta = JSON.parse(rawMeta);
      } catch {}

      let source: 'project' | 'chat' | undefined;
      let effectiveChatId = chatId;
      let effectiveProjectId = projectId;

      if (meta?.type === 'chat') {
        source = 'chat';
        effectiveChatId = meta?.id || chatId;
      } else if (meta?.type === 'project') {
        source = 'project';
        effectiveProjectId = meta?.id || projectId;
      } else if (chatId) {
        source = 'chat';
      } else if (projectId) {
        source = 'project';
      }

      let knowledgeBaseFolder = '—';
      let titleOrName = '';
      let trustEnabled = false;
      let osAccessEnabled = false;
      let modelsUsed: string[] = [];
      const modelsLabel = 'Based on Subscription Plan';

      if (source === 'chat' && effectiveChatId) {
        const { data: chatRow } = await supabase
          .from('chats')
          .select('id, title, knowledge_base_id, os_access_enabled, model_id, model_id_2')
          .eq('id', effectiveChatId)
          .maybeSingle();
        titleOrName = chatRow?.title || meta?.title || 'Chat';

        if (chatRow?.knowledge_base_id) {
          const { data: kbRow } = await supabase
            .from('knowledge_bases')
            .select('id, name')
            .eq('id', chatRow.knowledge_base_id)
            .maybeSingle();
          knowledgeBaseFolder = kbRow?.name || knowledgeBaseFolder;
        }

        try {
          const txt = await (await import('@os/utils/storage/VFS')).globalVFS.load(`${folder}\\.os-access.txt`);
          const json = await (await import('@os/utils/storage/VFS')).globalVFS.load(`${folder}\\.os-access.json`);
          if (txt && /enabled\s*=\s*true/i.test(txt)) osAccessEnabled = true;
          if (json) {
            const marker = JSON.parse(json);
            osAccessEnabled = !!marker?.enabled;
          }
          if (typeof chatRow?.os_access_enabled === 'boolean') {
            osAccessEnabled = chatRow.os_access_enabled;
          }
        } catch {}

        try {
          const { data: profile } = await supabase
            .from('profiles')
            .select('allow_analytics')
            .limit(1)
            .maybeSingle();
          trustEnabled = !!profile?.allow_analytics;
        } catch {}

        const baseModels: string[] = [];
        if (chatRow?.model_id) baseModels.push(String(chatRow.model_id));
        if (chatRow?.model_id_2) baseModels.push(String(chatRow.model_id_2));

        const { data: messageModels } = await supabase
          .from('messages')
          .select('model')
          .eq('chat_id', effectiveChatId)
          .not('model', 'is', null);

        const messageModelSet = new Set<string>();
        (messageModels || []).forEach((row: any) => {
          if (row?.model && typeof row.model === 'string') {
            messageModelSet.add(row.model);
          }
        });

        const combined = new Set<string>([...baseModels, ...Array.from(messageModelSet)]);
        const idsOrApiNames = Array.from(combined).filter(Boolean);
        let friendlyNames: string[] = [];

        if (idsOrApiNames.length > 0) {
          const { data: aiModels } = await supabase
            .from('ai_models')
            .select('id, name, apiName, provider');

          type ModelInfo = { name: string; provider?: string };
          const byId: Record<string, ModelInfo> = {};
          const byApi: Record<string, ModelInfo> = {};
          const byNameLower: Record<string, ModelInfo> = {};

          (aiModels || []).forEach((m: any) => {
            const info = { name: (m?.name || m?.apiName || '').trim(), provider: m?.provider || undefined };
            if (m?.id) byId[String(m.id)] = info;
            if (m?.apiName) byApi[String(m.apiName)] = info;
            if (m?.name) byNameLower[String(m.name).toLowerCase()] = info;
          });

          const uuidRe = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
          const fmt = (info?: ModelInfo, raw?: string) =>
            info ? (info.provider ? `${info.name} (${info.provider})` : info.name) : (raw || '');

          friendlyNames = idsOrApiNames.map((v) => {
            const raw = String(v).trim();
            if (uuidRe.test(raw) && byId[raw]) return fmt(byId[raw], raw);
            if (byApi[raw]) return fmt(byApi[raw], raw);
            const lower = raw.toLowerCase();
            if (byNameLower[lower]) return fmt(byNameLower[lower], raw);
            return raw;
          });
        }

        modelsUsed = Array.from(new Set(friendlyNames)).sort();

        setInfoContext({
          source: 'chat',
          chatTitle: titleOrName,
          knowledgeBaseFolder,
          modelsInfo: modelsLabel,
          trustEnabled,
          osAccessEnabled,
          modelsUsed,
          chatId: effectiveChatId,
          projectId: effectiveProjectId,
        });
        setShowInfoPane(true);
      }

      if (source === 'project' && effectiveProjectId) {
        const { data: projectRow } = await supabase
          .from('projects')
          .select('id, name, description, os_access_enabled')
          .eq('id', effectiveProjectId)
          .maybeSingle();
        titleOrName = projectRow?.name || meta?.name || 'Project';

        try {
          const { data: profile } = await supabase
            .from('profiles')
            .select('allow_analytics')
            .limit(1)
            .maybeSingle();
          trustEnabled = !!profile?.allow_analytics;
        } catch {}

        try {
          const txt = await (await import('@os/utils/storage/VFS')).globalVFS.load(`${folder}\\.os-access.txt`);
          const json = await (await import('@os/utils/storage/VFS')).globalVFS.load(`${folder}\\.os-access.json`);
          if (txt && /enabled\s*=\s*true/i.test(txt)) osAccessEnabled = true;
          if (json) {
            const marker = JSON.parse(json);
            osAccessEnabled = !!marker?.enabled;
          }
          if (typeof projectRow?.os_access_enabled === 'boolean') {
            osAccessEnabled = projectRow.os_access_enabled;
          }
        } catch {}

        setInfoContext({
          source: 'project',
          projectName: titleOrName,
          knowledgeBaseFolder,
          modelsInfo: modelsLabel,
          trustEnabled,
          osAccessEnabled,
          modelsUsed: [],
          chatId: effectiveChatId,
          projectId: effectiveProjectId,
        });
        setShowInfoPane(true);
      }
    };

    resolveContext();
  }, [filePath, chatId, projectId, supabase]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Custom extension to exit headings on Enter
  const ExitHeadingOnEnter = Extension.create({
    name: 'exitHeadingOnEnter',

    addKeyboardShortcuts() {
      return {
        Enter: ({ editor }) => {
          const { state } = editor;
          const { $from } = state.selection;

          // Check if we're in a heading
          if ($from.parent.type.name === 'heading') {
            const endPos = $from.end();
            const cursorPos = $from.pos;

            // If cursor is at or near the end of the heading
            if (cursorPos >= endPos - 1) {
              // Split and convert the new node to a paragraph
              return editor.commands.command(({ tr, state }) => {
                tr.split(endPos);
                tr.setBlockType(endPos + 1, endPos + 1, state.schema.nodes.paragraph);
                return true;
              });
            }
          }
          return false;
        },
      };
    },
  });

  // TipTap editor
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        link: {
          autolink: true,
          openOnClick: true,
          linkOnPaste: true,
        },
      }),
      Typography,
      UnderlineExtension,
      ResizableImage, // custom resizable image node view
      Placeholder.configure({ placeholder: 'Type here…' }),
      TextAlignExt.configure({ types: ['heading', 'paragraph'] }),
      TextStyle,
      FontFamily,
      FontSize,
      Color,
      Highlight.configure({ multicolor: true }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      CharacterCount,
      ChangeMark, // tracked changes mark
      ExitHeadingOnEnter, // Custom extension to exit headings on Enter
    ],
    content: htmlContent,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      setHtmlContent(html);
      setIsSaved(false);

      // Guards: skip tracking when we're applying marks or review mode is off
      if (isApplyingMarkRef.current || !reviewMode) {
        baselineTextRef.current = (editor.getText() || '').trim();
        return;
      }
      // Skip initial update so we don't mark the entire document
      if (!hasInitializedRef.current) {
        baselineTextRef.current = (editor.getText() || '').trim();
        hasInitializedRef.current = true;
        return;
      }

      trackInsertedChanges(editor);
    },
  });

  // Signal readiness when the editor is initialized so other apps can safely send content
  useEffect(() => {
    if (editor) {
      // Microtask timing ensures TipTap has applied its state
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent("mapleos-mapleword-ready"));
      }, 0);
    }
  }, [editor]);

  // Update stats whenever content changes
  useEffect(() => {
    const textContent = htmlContent.replace(/<[^>]*>/g, '');
    const words = textContent.trim().split(/\s+/).filter((w) => w.length > 0);
    setWordCount(words.length);
    setCharacterCount(textContent.length);
  }, [htmlContent]);

  // Load initial file if provided (now converts Markdown/text to HTML)
  useEffect(() => {
    const loadInitial = async () => {
      if (!filePath || !editor) return;

      mwLog('Initial load: filePath detected', filePath);

      // Prefer provided content, otherwise fetch from OS VFS
      let rawContent = initialContent || "";
      if (!rawContent) {
        await globalNavigator.waitForInitialization();
        await globalNavigator.syncWithVFS();
        rawContent = globalNavigator.findItemByPath(filePath)?.content ?? (await globalVFS.load(filePath)) ?? "";
      }

      if (typeof rawContent !== "string" || rawContent.trim().length === 0) {
        mwLog('No content found for filePath:', filePath);
        setCurrentFilePath(filePath);
        const fileName = filePath.split('\\').pop() || 'Untitled';
        setDocumentTitle(fileName.replace(/\.(html|mword|txt|md)$/i, ''));
        return;
      }

      const contentToLoad = await toDisplayHtml(rawContent);

      editor.commands.setContent(contentToLoad);
      setHtmlContent(contentToLoad);

      setCurrentFilePath(filePath);
      const fileName = filePath.split('\\').pop() || 'Untitled';
      setDocumentTitle(fileName.replace(/\.(html|mword|txt|md)$/i, ''));
    };

    loadInitial();
  }, [initialContent, filePath, editor]);

  // Resolve OS username from profile (fallback to email prefix)
  useEffect(() => {
    // If not logged in, consider username resolved (will use "User")
    if (!user) {
      setUsernameReady(true);
      return;
    }
    let cancelled = false;
    supabase
      .from('profiles')
      .select('username')
      .eq('id', user.id)
      .single()
      .then(({ data }) => {
        if (cancelled) return;
        const fallback = (user.email?.split('@')[0] || 'User');
        const candidate = (data?.username || fallback);
        const sanitized = (candidate.replace(/[^a-zA-Z0-9]/g, '') || 'User');
        setOsUsername(sanitized);
        setUsernameReady(true);
      })
      .catch(() => {
        // On any error, still mark ready with sanitized fallback
        const fallback = (user.email?.split('@')[0] || 'User');
        const sanitized = (fallback.replace(/[^a-zA-Z0-9]/g, '') || 'User');
        setOsUsername(sanitized);
        setUsernameReady(true);
      });
    return () => { cancelled = true; };
  }, [user]);

  // UPDATED: mapleos-mapleword-insert listener — insert content normally;
  // tracking is handled in onUpdate when reviewMode is true
  useEffect(() => {
    const handler = async (e: Event) => {
      const ce = e as CustomEvent<{ html?: string; text?: string; replace?: boolean; title?: string }>;
      const detail = ce.detail || {};
      const proposedTitle = detail.title;

      let payloadHtml = detail.html || "";
      if (!payloadHtml && detail.text) {
        payloadHtml = await toDisplayHtml(detail.text.trim());
      }
      if (!editor || !payloadHtml) return;

      try {
        if (proposedTitle) setDocumentTitle(proposedTitle);
        if (detail.replace) {
          editor.commands.setContent(payloadHtml);
        } else {
          editor.chain().focus().insertContent(payloadHtml).run();
        }
        if (reviewMode) setShowReviewPane(true);
        toast({ title: "Inserted", description: reviewMode ? "Change staged for review." : "Content inserted." });
      } catch (err) {
        console.error("MapleWord", "Insert error:", err);
        toast({ title: "Insert failed", description: "Could not insert content.", variant: "destructive" });
      }
    };
    window.addEventListener("mapleos-mapleword-insert", handler as EventListener);
    return () => {
      window.removeEventListener("mapleos-mapleword-insert", handler as EventListener);
    };
  }, [editor, toast, reviewMode]);

  // NEW: map text offsets to PM positions (unchanged)
  const getTextMap = (ed: typeof editor) => {
    const e = ed!;
    const { doc } = e.state;
    let text = '';
    const offsets: number[] = [];

    doc.descendants((node, nodePos) => {
      if (node.isText) {
        const t = node.text || '';
        for (let i = 0; i < t.length; i++) {
          text += t[i];
          offsets.push(nodePos + i);
        }
      } else if (node.isBlock) {
        if (text.length > 0) {
          text += '\n';
          offsets.push(nodePos);
        }
      }
      return true;
    });

    return { text, offsets };
  };

  // Boundary detector: whitespace or common punctuation/newline
  const isBoundaryChar = (ch: string) => /[\s\.\,\!\?\;\:\n]/.test(ch);

  // Commit pending insert: apply mark and add change entry
  const commitPendingInsert = () => {
    if (!editor) return;
    const pending = pendingInsertRef.current;
    if (!pending || pending.text.trim().length === 0) return;

    const pmFrom = pending.pmFrom;
    const pmTo = pmFrom + pending.text.length;

    const tr = editor.state.tr;
    isApplyingMarkRef.current = true;
    tr.addMark(pmFrom, pmTo, editor.state.schema.marks.changeMark.create({ id: `chg-${pmFrom}-${pmTo}`, type: 'insert' }));
    editor.view.dispatch(tr);
    setTimeout(() => { isApplyingMarkRef.current = false; }, 0);

    setChanges(prev => [
      ...prev,
      { id: `chg-${pmFrom}-${pmTo}`, type: 'insert', text: pending.text, from: pmFrom, to: pmTo, at: new Date().toISOString() },
    ]);
    setShowReviewPane(true);

    // Reset pending and idle timer
    pendingInsertRef.current = null;
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    // Update baseline to current text after commit
    baselineTextRef.current = (editor.getText() || '').trim();
  };

  // Schedule idle commit after short pause in typing
  const scheduleIdleCommit = (delayMs = 1200) => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = window.setTimeout(() => {
      commitPendingInsert();
    }, delayMs);
  };

  // NEW: Create a single change entry for AI-generated text after streaming completes
  const commitAIStreamedText = () => {
    if (!editor || aiStreamStartPosRef.current === null) return;

    const startPos = aiStreamStartPosRef.current;
    const currentText = editor.getText();
    const { offsets } = getTextMap(editor);

    // Find the end position of the streamed text
    const endPos = editor.state.doc.content.size;

    // Get the actual text that was inserted
    const insertedText = currentText.slice(startPos);

    if (insertedText.trim().length === 0) return;

    // Find the PM positions
    const pmFrom = offsets[startPos] || startPos;
    const pmTo = endPos;

    // Apply the change mark
    const tr = editor.state.tr;
    isApplyingMarkRef.current = true;
    tr.addMark(pmFrom, pmTo, editor.state.schema.marks.changeMark.create({
      id: `chg-ai-${pmFrom}-${pmTo}`,
      type: 'insert'
    }));
    editor.view.dispatch(tr);
    setTimeout(() => { isApplyingMarkRef.current = false; }, 0);

    // Add a single change entry for the entire AI-generated text
    setChanges(prev => [
      ...prev,
      {
        id: `chg-ai-${pmFrom}-${pmTo}`,
        type: 'insert',
        text: insertedText,
        from: pmFrom,
        to: pmTo,
        at: new Date().toISOString()
      },
    ]);
    setShowReviewPane(true);

    // Update baseline
    baselineTextRef.current = currentText.trim();

    // Reset AI streaming state
    isAIStreamingRef.current = false;
    aiStreamStartPosRef.current = null;
  };

  // Coalesce inserts into whole words/chunks until boundary/idle
  const trackInsertedChanges = (ed?: any) => {
    if (!ed || !editor) return;

    // Skip tracking during AI streaming - we'll create a single change entry when done
    if (isAIStreamingRef.current) {
      return;
    }

    const { text, offsets } = getTextMap(editor);
    const prev = baselineTextRef.current || '';
    const curr = text;

    if (curr === prev) return;

    // Find first differing index from baseline
    let start = 0;
    for (; start < Math.min(prev.length, curr.length); start++) {
      if (prev[start] !== curr[start]) break;
    }

    // Find tail match length
    let endTail = 0;
    const maxTail = Math.min(prev.length - start, curr.length - start);
    for (; endTail < maxTail; endTail++) {
      if (prev[prev.length - 1 - endTail] !== curr[curr.length - 1 - endTail]) break;
    }

    const insertedText = curr.slice(start, curr.length - endTail);
    const insertedLen = insertedText.length;

    // Ignore pure whitespace insertions
    if (curr.length >= prev.length && insertedLen > 0 && insertedText.replace(/\s+/g, '').length > 0) {
      const pmFrom = offsets[start] || 0;

      // If we already have a pending insert, see if we can extend it
      const pending = pendingInsertRef.current;
      if (pending && pending.startIdx === start && insertedText.startsWith(pending.text)) {
        // Extend pending chunk and reschedule commit
        pendingInsertRef.current = { ...pending, text: insertedText, lastAt: Date.now() };
        if (isBoundaryChar(insertedText[insertedText.length - 1]) || insertedText.length >= 30) {
          commitPendingInsert();
        } else {
          scheduleIdleCommit();
        }
        return;
      }

      // Commit any previous pending chunk before starting a new one
      if (pending) {
        commitPendingInsert();
      }

      // Start a new pending chunk
      pendingInsertRef.current = { startIdx: start, pmFrom, text: insertedText, lastAt: Date.now() };

      // If the inserted chunk already ends at a boundary or is large (e.g., AI paste), commit immediately
      if (isBoundaryChar(insertedText[insertedText.length - 1]) || insertedText.length >= 30 || /\s/.test(insertedText)) {
        commitPendingInsert();
      } else {
        scheduleIdleCommit();
      }
      return;
    }

    // Handle deletions later; for now update baseline to avoid drift
    baselineTextRef.current = curr;
  };

  // Helper: log review change to Supabase
  const logReviewChange = (ch: { id: string; type: 'insert' | 'delete'; text: string; from: number; to: number }, action: 'accept' | 'reject' | 'accept_all' | 'reject_all') => {
    if (!user) return;
    const filePathToSave = currentFilePath || null;
    supabase.from('mapleword_reviews').insert({
      user_id: user.id,
      file_path: filePathToSave,
      document_title: documentTitle,
      change_id: ch.id,
      change_type: ch.type,
      change_text: ch.text,
      action,
      from_pos: ch.from,
      to_pos: ch.to
    });
  };

  // Accept/Reject handlers for tracked changes (unchanged)
  const acceptRevision = () => {
    if (!pendingRevision || !editor) return;
    const { proposedHtml, replace, proposedTitle } = pendingRevision;

    if (proposedTitle) {
      setDocumentTitle(proposedTitle);
    }
    if (replace) {
      editor.commands.setContent(proposedHtml);
    } else {
      editor.chain().focus().insertContent(proposedHtml).run();
    }
    setPendingRevision(null);
    setShowReviewPane(false);
    //toast({ title: 'Change accepted', description: 'The proposed change has been applied.' });
  };

  const rejectRevision = () => {
    setPendingRevision(null);
    setShowReviewPane(false);
    //toast({ title: 'Change rejected', description: 'The proposed change was discarded.' });
  };

  const previewHTML = () => {
    const html = editor?.getHTML() || '';
    const win = window.open('', '_blank');
    if (win) {
      win.document.write(`
        <html>
          <head>
            <title>${documentTitle} — Preview</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 40px; line-height: 1.6; }
              h1, h2, h3 { color: #333; }
              p { margin-bottom: 1em; }
              img { max-width: 100%; height: auto; }
            </style>
          </head>
          <body>${html}</body>
        </html>
      `);
      win.document.close();
    }
  };

  const handleExportDocx = async () => {
    const html = editor?.getHTML() ?? '';
    const wrapped = `
      <html>
        <head><meta charset="utf-8"></head>
        <body>${html}</body>
      </html>
    `;
    const mod: any = await import('html-docx-js-typescript');
    const toBlob = mod?.asBlob || mod?.default?.asBlob;
    if (!toBlob) {
      toast({ title: 'Export Failed', description: 'DOCX converter is unavailable.', variant: 'destructive' });
      return;
    }
    const blob: Blob = toBlob(wrapped);
    // Download to the user's machine
    saveAs(blob, `${documentTitle || 'Document'}.docx`);
    // Also save into the OS VFS so it shows in Documents
    await saveDocxToVFSAndSupabase(blob);
    toast({ title: 'Export Complete', description: 'Document exported as Word (.docx) and saved to Documents.' });
  };

  const toggleFullscreen = async () => {
    if (!document.fullscreenElement) {
      if (containerRef.current) {
        try {
          await containerRef.current.requestFullscreen();
        } catch (err) {
          console.error("Error entering fullscreen:", err);
        }
      }
    } else {
      try {
        await document.exitFullscreen();
      } catch (err) {
        console.error("Error exiting fullscreen:", err);
      }
    }
  };
  const toggleCodeView = () => setShowCode((v) => !v);

  // Clear formatting utility (same behavior as the ribbon)
  const clearFormatting = () => {
    editor
      ?.chain()
      .focus()
      .unsetAllMarks()
      .unsetTextAlign()
      .setParagraph()
      .run();
  };

  // Focus editor and set caret near clicked coordinates
  const focusEditorAtCoords = (clientX: number, clientY: number) => {
    if (!editor?.view) return;
    const result = editor.view.posAtCoords({ left: clientX, top: clientY });
    const pos = result?.pos;
    if (typeof pos === 'number') {
      editor.chain().focus().setTextSelection(pos).run();
    } else {
      // If not inside content, place caret at end
      const endPos = editor.state.doc.content.size;
      editor.chain().focus().setTextSelection(endPos).run();
    }
  };

  // Handle clicks anywhere in the canvas (outside the ProseMirror content too)
  const handleCanvasMouseDown = (e: React.MouseEvent) => {
    if (!editor) return;
    if (e.button !== 0) return; // only left click
    const target = e.target as HTMLElement;
    const insideProse = !!target.closest('.ProseMirror');
    if (insideProse) return; // let ProseMirror handle its own clicks
    e.preventDefault();
    focusEditorAtCoords(e.clientX, e.clientY);
  };

  // Helper: convert Blob to base64 for VFS/Supabase storage
  const blobToBase64 = async (blob: Blob): Promise<string> => {
    const arrayBuffer = await blob.arrayBuffer();
    let binary = '';
    const bytes = new Uint8Array(arrayBuffer);
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  };

  // Resolve a reasonable 'Documents' path for autosave
  const getUserDocumentsPath = () => {
    return `C:\\User\\${osUsername}\\Documents`;
  };

  // File handling (Save / Save As) unchanged...
  const handleSave = async () => {
    mwLog('handleSave() path =', currentFilePath);
    if (currentFilePath) {
      await saveToVFSAndSupabase(currentFilePath);
    } else {
      handleSaveAs();
    }
  };

  const handleSaveAs = () => {
    mwLog('handleSaveAs()');
    const base = documentTitle.trim();
    const stripped = base.replace(/\.(html|txt|docx)$/i, '');
    const next = stripped.toLowerCase().endsWith('.mword') ? stripped : `${stripped}.mword`;
    setSaveFileName(next);
    setShowSaveDialog(true);
  };

  const handleSaveLocation = async (selectedFolder: any) => {
    const rawName = saveFileName || `${documentTitle}.mword`;
    const fileName = rawName.toLowerCase().endsWith('.mword')
      ? rawName
      : `${rawName.replace(/\.(html|txt|docx)$/i, '')}.mword`;
    const base = (selectedFolder?.path || selectedFolder?.file_path || 'C:\\').trim();
    const basePath = base.endsWith('\\') ? base : `${base}\\`;
    const filePath = `${basePath}${fileName}`;
    // Update UI title to match saved filename (without extension) like MapleSlide
    setDocumentTitle(fileName.replace(/\.mword$/i, ''));
    await saveToVFSAndSupabase(filePath, basePath.slice(0, -1));
    setShowSaveDialog(false);
  };

  const saveToVFSAndSupabase = async (path: string, parentPath?: string, opts?: { silent?: boolean }) => {
    mwLog('saveToVFSAndSupabase()', path);
    if (!opts?.silent) setIsSaving(true);
    try {
      const { globalVFS } = await import('@os/utils/storage/VFS');
      const content = editor?.getHTML() ?? '';
      // Ensure parent directory chain exists (e.g. MapleWord\\Drafts)
      const dir = parentPath || path.substring(0, path.lastIndexOf('\\'));
      await ensureDir(dir);

      await globalVFS.save(path, content);

      window.dispatchEvent(
        new CustomEvent('fileSystemChanged', {
          detail: { path: dir },
        })
      );

      if (user) {
        await saveToSupabase(path, dir, content, 'application/x-mapleword');
      }

      setCurrentFilePath(path);
      const fileName = path.split('\\').pop() || 'Untitled.mword';
      // indicate persistently that the document is saved until the next change
      setIsSaved(true);
      
      // Auto-push vectors to all linked projects for this document
      if (user) {
        const linkedProjects = await supabase
          .from('mapleword_project_documents')
          .select('project_id')
          .eq('user_id', user.id)
          .eq('file_path', path);
        if (!('error' in linkedProjects) && Array.isArray(linkedProjects.data)) {
          for (const row of linkedProjects.data) {
            if (row.project_id) {
              await pushDocToProjectKBFor(row.project_id, 'open');
            }
          }
        } else if (projectId) {
          // Fallback: push to current projectId or newly linked projectId
          await pushDocToProjectKBFor(projectId, 'open');
        } else if (linkedProjectId) {
          await pushDocToProjectKBFor(linkedProjectId, 'open');
        }
      }
      
      // Push to KB if linked to a project
      if (projectId || linkedProjectId) {
        await pushDocToProjectKB('open');
      }
    } catch (error) {
      console.error(MW_LOG_PREFIX, 'Save error:', error);
      toast({
        title: 'Save Failed',
        description: 'Failed to save the document. Please try again.',
        variant: 'destructive',
      });
    } finally {
      if (!opts?.silent) setIsSaving(false);
    }
  };

  const saveToSupabase = async (path: string, parentPath: string | undefined, content: string, contentType?: string) => {
    if (!user) return;

    mwLog('saveToSupabase()', path);
    try {
      const fileName = path.split('\\').pop() || 'Untitled.html';
      const parent = parentPath || path.substring(0, path.lastIndexOf('\\'));

      const { data: existingFile } = await supabase
        .from('os_user_files')
        .select('id')
        .eq('user_id', user.id)
        .eq('file_path', path)
        .maybeSingle();

      let result;
      if (existingFile) {
        result = await supabase
          .from('os_user_files')
          .update({
            content,
            size_bytes: content.length,
            updated_at: new Date().toISOString(),
            ...(contentType ? { content_type: contentType } : {})
          })
          .eq('id', existingFile.id)
          .eq('user_id', user.id);
      } else {
        result = await supabase.from('os_user_files').insert({
          user_id: user.id,
          file_path: path,
          file_name: fileName,
          parent_path: parent,
          content,
          content_type: contentType || 'text/plain',
          size_bytes: content.length,
          is_directory: false,
        });
      }

      if ((result as any).error) throw (result as any).error;

      setCurrentFilePath(path);

    } catch (error) {
      console.error(MW_LOG_PREFIX, 'SaveToSupabase error:', error);
      toast({
        title: 'Save Failed',
        description: 'Failed to save the document. Please try again.',
        variant: 'destructive',
      });
    }
  };

  // NEW: Save exported .docx into VFS and Supabase so it appears in Documents
  const saveDocxToVFSAndSupabase = async (blob: Blob) => {
    try {
      const docs = await getDocumentsPath();
      const baseDir = `${docs}\\MapleWord\\Exports`;
      const fileName = `${(documentTitle || 'Document').replace(/[/\\:?*"<>|]/g, '')}.docx`;
      const filePath = `${baseDir}\\${fileName}`;
      const base64 = await blobToBase64(blob);
      const { globalVFS } = await import('@os/utils/storage/VFS');
      // Ensure full directory chain (MapleWord and Exports) exists
      await ensureDir(baseDir);

      // Save into VFS
      await globalVFS.save(filePath, base64);
      // Notify File Explorer
      window.dispatchEvent(new CustomEvent('fileSystemChanged', { detail: { path: baseDir } }));
      // Save into Supabase (if logged in)
      if (user) {
        await saveToSupabase(
          filePath,
          baseDir,
          base64,
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        );
      }
      toast({ title: 'Saved to Documents', description: `Exported to: MapleWord\\Exports\\${fileName}` });
    } catch (error) {
      console.error(MW_LOG_PREFIX, 'SaveDocx error:', error);
      toast({
        title: 'Export Save Failed',
        description: 'Could not save the .docx to Documents.',
        variant: 'destructive',
      });
    }
  };

  const handleLoad = () => {
    mwLog('handleLoad()');
    setShowFileBrowser(true);
  };

  const handleFileSelect = async (file: any) => {
    mwLog('handleFileSelect()', file);
    if (!file.content) {
      toast({
        title: 'Error',
        description: 'Selected file has no content.',
        variant: 'destructive',
      });
      return;
    }
    setIsLoading(true);
    try {
      const raw = file.content as string;
      const contentToLoad = await toDisplayHtml(raw);

      editor?.commands.setContent(contentToLoad);
      setHtmlContent(contentToLoad);

      setDocumentTitle(
        (file.file_name || 'Untitled')
          .replace(/\.(mword|html|txt|md)$/i, '')
      );
      setCurrentFilePath(file.file_path);
      toast({
        title: 'Document Loaded',
        description: `Loaded: ${file.file_name}`,
      });
    } catch (error) {
      console.error(MW_LOG_PREFIX, 'Load error:', error);
      toast({
        title: 'Load Failed',
        description: 'Failed to load the document.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportHTML = () => {
    const blob = new Blob([editor?.getHTML() ?? ''], { type: 'text/html' });
    saveAs(blob, `${documentTitle}.html`);
    toast({
      title: 'Export Complete',
      description: 'Document exported as HTML file.',
    });
  };

  const handleExportText = () => {
    const textContent = (editor?.getText() ?? '').trim();
    const blob = new Blob([textContent], { type: 'text/plain' });
    saveAs(blob, `${documentTitle}.txt`);
    toast({
      title: 'Export Complete',
      description: 'Document exported as text file.',
    });
  };

  const handleNewDocument = () => {
    const newContent = '<p>Start writing your new document here…</p>';
    editor?.commands.setContent(newContent);
    setDocumentTitle('Untitled Document');
    toast({
      title: 'New Document',
      description: 'Started a new document.',
    });
  };

  // Autosave while typing (debounced)
  useEffect(() => {
    if (!currentFilePath) return; // wait until first Save/Save As sets a path
    const parent = currentFilePath.substring(0, currentFilePath.lastIndexOf('\\'));
    const timer = setTimeout(async () => {
      setAutoSaving(true);
      await saveToVFSAndSupabase(currentFilePath, parent, { silent: true });
      setAutoSaving(false);
    }, 1500); // debounce ~1.5s after last change
    return () => clearTimeout(timer);
  }, [htmlContent, currentFilePath]);

  // Initial autosave for untitled documents (like Google Docs)
  // If there is no currentFilePath yet and the user is typing, create a draft path and save.
  useEffect(() => {
    if (currentFilePath) return; // already have a path
    const hasContent = (htmlContent || '').trim().length > 0;
    if (!hasContent || !usernameReady) return;

    const timer = setTimeout(async () => {
      const docs = await getDocumentsPath();
      const draftsPath = `${docs}\\MapleWord\\Drafts`;
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const defaultName = 'Untitled';
      const filePath = `${draftsPath}\\${defaultName}-${timestamp}.mword`;
      mwLog('Autosave: creating initial draft at', filePath);
      await saveToVFSAndSupabase(filePath, draftsPath, { silent: true });
    }, 1500); // wait briefly after typing begins

    return () => clearTimeout(timer);
  }, [htmlContent, currentFilePath, usernameReady, osUsername]);

  // NEW: changes state (list) and helpers
  const acceptChange = (id: string) => {
    if (!editor) return;
    const ch = changes.find(c => c.id === id);
    if (!ch) return;

    // Log acceptance before mutating state
    logReviewChange(ch, 'accept');

    const tr = editor.state.tr;
    tr.removeMark(ch.from, ch.to, editor.state.schema.marks.changeMark);
    editor.view.dispatch(tr);

    setChanges(prev => prev.filter(c => c.id !== id));
    baselineTextRef.current = (editor.getText() || '').trim();

    toast({ title: 'Change accepted', description: 'Inserted text kept.' });
  };

  const rejectChange = (id: string) => {
    if (!editor) return;
    const ch = changes.find(c => c.id === id);
    if (!ch) return;

    // Log rejection before mutating state
    logReviewChange(ch, 'reject');

    const tr = editor.state.tr.delete(ch.from, ch.to);
    editor.view.dispatch(tr);

    setChanges(prev => prev.filter(c => c.id !== id));
    baselineTextRef.current = (editor.getText() || '').trim();

    toast({ title: 'Change rejected', description: 'Inserted text removed.' });
  };

  const acceptAll = () => {
    if (!editor) return;

    // Log all before mutation
    changes.forEach(ch => logReviewChange(ch, 'accept_all'));

    const tr = editor.state.tr;
    changes.forEach(ch => {
      tr.removeMark(ch.from, ch.to, editor.state.schema.marks.changeMark);
    });
    editor.view.dispatch(tr);
    setChanges([]);
    baselineTextRef.current = (editor.getText() || '').trim();

    toast({ title: 'All changes accepted', description: 'All inserted text kept.' });
  };

  const rejectAll = () => {
    if (!editor) return;

    // Log all before mutation
    changes.forEach(ch => logReviewChange(ch, 'reject_all'));

    const tr = editor.state.tr;
    const sorted = [...changes].sort((a, b) => b.from - a.from);
    sorted.forEach(ch => {
      tr.delete(ch.from, ch.to);
    });
    editor.view.dispatch(tr);
    setChanges([]);
    baselineTextRef.current = (editor.getText() || '').trim();

    toast({ title: 'All changes rejected', description: 'All inserted text removed.' });
  };

  // Right-click context menu: Copy / Paste
  const showEditorContextMenu = (e: React.MouseEvent) => {
    if (!editor) return;
    e.preventDefault();
    const { from, to } = editor.state.selection;
    const hasSelection = to > from;
    const isInTable = editor.isActive('table');

    const items: ContextMenuItem[] = [
      {
        id: 'copy',
        label: 'Copy',
        icon: <CopyIcon size={16} />,
        disabled: !hasSelection,
        onClick: async () => {
          const text = editor.state.doc.textBetween(from, to, '\n');
          try {
            await navigator.clipboard.writeText(text);
            toast({ title: 'Copied', description: 'Selection copied to clipboard.' });
          } catch {
            // fallback
            const textarea = document.createElement('textarea');
            textarea.value = text;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
          }
        }
      },
      {
        id: 'paste',
        label: 'Paste',
        icon: <PasteIcon size={16} />,
        onClick: async () => {
          try {
            const clip = await navigator.clipboard.readText();
            if (clip) {
              editor.chain().focus().insertContent(clip).run();
            }
          } catch {
            toast({ title: 'Cannot paste', description: 'Browser blocked clipboard access. Use Ctrl/Cmd+V.', variant: 'destructive' });
          }
        }
      },
    ];

    // Table-specific actions appear only when inside a table
    if (isInTable) {
      items.push(
        {
          id: 'row-add',
          label: 'Add Row After',
          icon: <Rows size={16} />,
          disabled: !editor.can().addRowAfter(),
          onClick: () => editor.chain().focus().addRowAfter().run(),
        },
        {
          id: 'col-add',
          label: 'Add Column After',
          icon: <Columns size={16} />,
          disabled: !editor.can().addColumnAfter(),
          onClick: () => editor.chain().focus().addColumnAfter().run(),
        },
        {
          id: 'row-del',
          label: 'Delete Row',
          icon: <Rows size={16} />,
          disabled: !editor.can().deleteRow(),
          onClick: () => editor.chain().focus().deleteRow().run(),
        },
        {
          id: 'col-del',
          label: 'Delete Column',
          icon: <Columns size={16} />,
          disabled: !editor.can().deleteColumn(),
          onClick: () => editor.chain().focus().deleteColumn().run(),
        },
        {
          id: 'toggle-header',
          label: 'Toggle Header Row',
          disabled: !editor.can().toggleHeaderRow(),
          onClick: () => editor.chain().focus().toggleHeaderRow().run(),
        },
        {
          id: 'merge-cells',
          label: 'Merge Cells',
          icon: <Merge size={16} />,
          disabled: !editor.can().mergeCells(),
          onClick: () => editor.chain().focus().mergeCells().run(),
        },
        {
          id: 'split-cell',
          label: 'Split Cell',
          icon: <Split size={16} />,
          disabled: !editor.can().splitCell(),
          onClick: () => editor.chain().focus().splitCell().run(),
        },
        {
          id: 'delete-table',
          label: 'Delete Table',
          icon: <Trash2 size={16} />,
          disabled: !editor.can().deleteTable(),
          onClick: () => editor.chain().focus().deleteTable().run(),
        },
      );
    }

    showContextMenu(e, items);
  };

  // Helper: upsert document status in Supabase
  const upsertProjectDocumentStatus = async (newStatus: 'open' | 'completed') => {
    if (!user) {
      toast({ title: 'Not signed in', description: 'Please sign in to update document status.', variant: 'destructive' });
      return;
    }
    const effectiveProjectId = projectId || linkedProjectId;
    if (!effectiveProjectId) {
      // Show link dialog to choose a project
      setShowLinkDialog(true);
      return;
    }
    const path = currentFilePath || filePath;
    if (!path) {
      toast({ title: 'Save required', description: 'Please save the document before marking it completed.', variant: 'destructive' });
      return;
    }

    const { data: existing } = await supabase
      .from('mapleword_project_documents')
      .select('id')
      .eq('user_id', user.id)
      .eq('project_id', effectiveProjectId)
      .eq('file_path', path)
      .maybeSingle();

    const payload = {
      user_id: user.id,
      project_id: effectiveProjectId,
      chat_id: infoContext.chatId || null,
      file_path: path,
      document_title: documentTitle,
      status: newStatus,
      locked: newStatus === 'completed',
      updated_at: new Date().toISOString(),
      completed_at: newStatus === 'completed' ? new Date().toISOString() : null
    };

    let result;
    if (existing?.id) {
      result = await supabase
        .from('mapleword_project_documents')
        .update(payload)
        .eq('id', existing.id);
    } else {
      result = await supabase
        .from('mapleword_project_documents')
        .insert(payload);
    }

    if ((result as any)?.error) {
      toast({ title: 'Status update failed', description: 'Could not update document status.', variant: 'destructive' });
    } else {
      setIsLocked(newStatus === 'completed');
      editor?.setEditable(!(newStatus === 'completed'));
      toast({ title: newStatus === 'completed' ? 'Document Completed' : 'Document Reopened', description: newStatus === 'completed' ? 'Editing is now locked.' : 'Editing unlocked.' });
      if (newStatus === 'completed') {
        await pushDocToProjectKBFor(effectiveProjectId, 'completed');
      }
    }
  };

  // Fetch status when known project/path
  useEffect(() => {
    const fetchStatus = async () => {
      if (!user) return;
      const path = currentFilePath || filePath;
      if (!path) return;
      const { data, error } = await supabase
        .from('mapleword_project_documents')
        .select('locked')
        .eq('user_id', user.id)
        .eq('file_path', path);
      if (!error && Array.isArray(data)) {
        const anyLocked = data.some(d => !!d.locked);
        setIsLocked(anyLocked);
        if (anyLocked) {
          editor?.setEditable(false);
        }
      }
    };
    fetchStatus();
  }, [user, currentFilePath, filePath, editor]);

  // Hook save to push doc to KB when linked to a project
  // inside saveToVFSAndSupabase success path:
  // setIsSaved(true);
  // if (projectId || linkedProjectId) { await pushDocToProjectKB('open'); }

  // Helper: ensure a knowledge base exists for the project and return its ID
  const ensureProjectKnowledgeBase = async (projId: string): Promise<string | null> => {
    if (!user) return null;
    const kbName = `Project ${projId} KB`;
    const { data: kbExisting } = await supabase
      .from('knowledge_bases')
      .select('id, name')
      .eq('created_by', user.id)
      .eq('name', kbName)
      .maybeSingle();
    if (kbExisting?.id) return kbExisting.id;
    const { data: kbNew, error } = await supabase
      .from('knowledge_bases')
      .insert({
        name: kbName,
        description: `Knowledge base for project ${projId}`,
        organization_id: null,
        created_by: user.id,
        is_private: true,
      })
      .select('id')
      .single();
    if (error) {
      console.error('[MapleWord] Failed to create project KB:', error);
      toast({ title: 'KB error', description: 'Could not create project knowledge base.', variant: 'destructive' });
      return null;
    }
    return kbNew?.id ?? null;
  };

  // Push current document into KB with embedding (insert or update)
  const pushDocToProjectKB = async (status: 'open' | 'completed') => {
    try {
      const effectiveProjectId = projectId || linkedProjectId;
      if (!user || !effectiveProjectId) return;
      const kbId = await ensureProjectKnowledgeBase(effectiveProjectId);
      if (!kbId) return;

      const contentText = (editor?.getText() || '').trim();
      if (!contentText) return;

      const { generateEmbedding } = await import('@/lib/embeddings');
      const embedding = await generateEmbedding(contentText);

      const path = currentFilePath || filePath || '';
      const fileType = 'markdown';
      const size = contentText.length;
      const name = documentTitle || 'MapleWord Document';

      const { data: existingDoc } = await supabase
        .from('knowledge_base_documents')
        .select('id')
        .eq('knowledge_base_id', kbId)
        .eq('storage_path', path)
        .maybeSingle();

      const payload: any = {
        knowledge_base_id: kbId,
        name,
        content: contentText,
        file_type: fileType,
        size,
        storage_path: path || null,
        updated_at: new Date().toISOString(),
      };
      if (embedding) payload.embedding = embedding;

      let resp;
      if (existingDoc?.id) {
        resp = await supabase
          .from('knowledge_base_documents')
          .update(payload)
          .eq('id', existingDoc.id)
          .select('id')
          .single();
      } else {
        resp = await supabase
          .from('knowledge_base_documents')
          .insert(payload)
          .select('id')
          .single();
      }

      if ((resp as any)?.error) {
        console.error('[MapleWord] KB upsert error:', (resp as any).error);
        toast({ title: 'KB update failed', description: 'Could not store document vector.', variant: 'destructive' });
        return;
      }
      toast({ title: 'Project KB updated', description: status === 'completed' ? 'Completed doc stored with vector.' : 'Saved doc stored with vector.' });
    } catch (err) {
      console.error('[MapleWord] pushDocToProjectKB error:', err);
      toast({ title: 'KB error', description: 'Failed to push document to project KB.', variant: 'destructive' });
    }
  };

  // Replace single-project KB helper with per-project version
  const pushDocToProjectKBFor = async (projId: string, status: 'open' | 'completed') => {
    try {
      if (!user || !projId) return;
      const kbId = await ensureProjectKnowledgeBase(projId);
      if (!kbId) return;

      const contentText = (editor?.getText() || '').trim();
      if (!contentText) return;

      const { generateEmbedding } = await import('@/lib/embeddings');
      const embedding = await generateEmbedding(contentText);

      const path = currentFilePath || filePath || '';
      const fileType = 'markdown';
      const size = contentText.length;
      const name = documentTitle || 'MapleWord Document';

      const { data: existingDoc } = await supabase
        .from('knowledge_base_documents')
        .select('id')
        .eq('knowledge_base_id', kbId)
        .eq('storage_path', path)
        .maybeSingle();

      const payload: any = {
        knowledge_base_id: kbId,
        name,
        content: contentText,
        file_type: fileType,
        size,
        storage_path: path || null,
        updated_at: new Date().toISOString(),
      };
      if (embedding) payload.embedding = embedding;

      let resp;
      if (existingDoc?.id) {
        resp = await supabase
          .from('knowledge_base_documents')
          .update(payload)
          .eq('id', existingDoc.id)
          .select('id')
          .single();
      } else {
        resp = await supabase
          .from('knowledge_base_documents')
          .insert(payload)
          .select('id')
          .single();
      }

      if ((resp as any)?.error) {
        console.error('[MapleWord] KB upsert error:', (resp as any).error);
        toast({ title: 'KB update failed', description: 'Could not store document vector.', variant: 'destructive' });
        return;
      }
      toast({ title: 'Project KB updated', description: status === 'completed' ? 'Completed doc stored with vector.' : 'Saved doc stored with vector.' });
    } catch (err) {
      console.error('[MapleWord] pushDocToProjectKBFor error:', err);
      toast({ title: 'KB error', description: 'Failed to push document to project KB.', variant: 'destructive' });
    }
  };

  // NEW: open Completed flow (open modal when no project is linked)
  const handleCompletedClick = async () => {
    // Use incoming prop projectId or user-chosen link
    const effectiveProject = projectId || linkedProjectId;
    if (!effectiveProject) {
      setShowLinkDialog(true);
      return;
    }
    await finalizeComplete(effectiveProject);
  };

  // NEW: finalize completion by linking + locking
  const finalizeComplete = async (projId: string) => {
    if (!user) {
      toast({ title: 'Not signed in', description: 'Sign in to complete the document.', variant: 'destructive' });
      return;
    }
    const path = currentFilePath || filePath || '';
    if (!path) {
      toast({ title: 'Save required', description: 'Please save the document before marking it completed.', variant: 'destructive' });
      return;
    }

    // Upsert link row
    const payload: any = {
      user_id: user.id,
      project_id: projId,
      chat_id: infoContext.chatId || null,
      file_path: path,
      document_title: documentTitle,
      status: 'completed',
      locked: true,
      updated_at: new Date().toISOString(),
      completed_at: new Date().toISOString()
    };

    // Check existing by (user_id, project_id, file_path)
    const { data: existing } = await supabase
      .from('mapleword_project_documents')
      .select('id')
      .eq('user_id', user.id)
      .eq('project_id', projId)
      .eq('file_path', path)
      .maybeSingle();

    let result;
    if (existing?.id) {
      result = await supabase
        .from('mapleword_project_documents')
        .update(payload)
        .eq('id', existing.id);
    } else {
      result = await supabase
        .from('mapleword_project_documents')
        .insert(payload);
    }

    if ((result as any)?.error) {
      toast({ title: 'Failed to link', description: 'Could not link document to the project.', variant: 'destructive' });
      return;
    }

    setLinkedProjectId(projId);
    setIsLocked(true);
    editor?.setEditable(false);
    setShowLinkDialog(false);
    toast({ title: 'Document Completed', description: 'Linked to project and locked.' });
  };

  // Allow linking to MORE projects when already locked
  const linkDocumentToProject = async (projId: string) => {
    if (!user) return;
    const path = currentFilePath || filePath || '';
    if (!path) {
      toast({ title: 'Save required', description: 'Please save the document first.', variant: 'destructive' });
      return;
    }
    const payload: any = {
      user_id: user.id,
      project_id: projId,
      chat_id: infoContext.chatId || null,
      file_path: path,
      document_title: documentTitle,
      status: 'completed',
      locked: true,
      updated_at: new Date().toISOString(),
      completed_at: new Date().toISOString()
    };

    const { data: existing } = await supabase
      .from('mapleword_project_documents')
      .select('id')
      .eq('user_id', user.id)
      .eq('project_id', projId)
      .eq('file_path', path)
      .maybeSingle();

    let result;
    if (existing?.id) {
      result = await supabase
        .from('mapleword_project_documents')
        .update(payload)
        .eq('id', existing.id);
    } else {
      result = await supabase
        .from('mapleword_project_documents')
        .insert(payload);
    }

    if ((result as any)?.error) {
      toast({ title: 'Failed to link', description: 'Could not link document to the project.', variant: 'destructive' });
      return;
    }
    setLinkedProjectId(projId);
    setIsLocked(true);
    editor?.setEditable(false);
    setShowLinkDialog(false);
    // Push vector for this project
    await pushDocToProjectKBFor(projId, 'completed');
    toast({ title: 'Linked', description: 'Document linked to project and remains locked.' });
  };

  return (
    <div
      ref={containerRef}
      className={`flex flex-col h-full bg-background ${isFullscreen ? 'p-4 overflow-y-auto' : ''}`}
      data-window-interactive="true"
      onPointerDownCapture={(e) => e.stopPropagation()}
      onContextMenu={hideContextMenu}
    >
      {/* Header */}
      <div className="border-b border-border p-4 pb-2 relative z-20" data-window-interactive="true">

        {/* Row 1: Title + stats + simple view toggles */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-4">
            <Input
              ref={titleInputRef}
              value={documentTitle}

              onChange={(e) => setDocumentTitle(e.target.value)}
              onKeyDown={async (e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  
                  const docs = await getDocumentsPath();
                  const safeTitle = documentTitle.replace(/[/\\?%*:|"<>]/g, '-').trim() || 'Untitled';
                  const fileName = safeTitle.toLowerCase().endsWith('.mword') 
                    ? safeTitle 
                    : `${safeTitle}.mword`;
                  
                  // Determine target directory:
                  // 1. If we have a current path and it's NOT a draft, keep the same folder.
                  // 2. Otherwise (new file or draft), default to Documents\MapleWord.
                  let targetDir = `${docs}\\MapleWord`;
                  if (currentFilePath && !currentFilePath.includes('\\Drafts\\')) {
                    targetDir = currentFilePath.substring(0, currentFilePath.lastIndexOf('\\'));
                  }
                  
                  const targetPath = `${targetDir}\\${fileName}`;

                  if (currentFilePath !== targetPath) {
                    // Save as new file (rename/move behavior from user perspective)
                    await saveToVFSAndSupabase(targetPath);
                    toast({ 
                      title: "Saved", 
                      description: `Document saved as ${fileName}`,
                      action: (
                        <ToastAction 
                          altText="Open Folder" 
                          onClick={() => onOpenWindow?.('file-explorer', 'File Explorer', targetDir)}
                        >
                          Open Folder
                        </ToastAction>
                      ),
                    });
                  } else {
                    // Just update content
                    await handleSave();
                  }

                  // Remove focus ring from title input and focus editor
                  titleInputRef.current?.blur();
                  editor?.chain().focus().run();
                }
              }}
              className="max-w-64"
              placeholder="Document name"
            />
            {/* REMOVED: inline word/char counter near title; moved to footer status bar */}
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" size="sm" variant="outline" onMouseDown={(e) => { e.preventDefault(); previewHTML(); }}>
              <Eye className="w-3 h-3 mr-1" /> Preview
            </Button>
            <Button type="button" size="sm" variant="outline" onMouseDown={(e) => { e.preventDefault(); toggleFullscreen(); }}>
              {isFullscreen ? <Minimize2 className="w-3 h-3 mr-1" /> : <Maximize2 className="w-3 h-3 mr-1" />} Fullscreen
            </Button>
            <Button type="button" size="sm" variant={showCode ? 'secondary' : 'outline'} onMouseDown={(e) => { e.preventDefault(); toggleCodeView(); }}>
              <Code2 className="w-3 h-3 mr-1" /> Code
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onMouseDown={(e) => { e.preventDefault(); clearFormatting(); }}
              title="Clear formatting"
            >
              <Eraser className="w-3 h-3 mr-1" /> Clear
            </Button>
            <Button
              type="button"
              size="icon"
              variant="outline"
              onMouseDown={(e) => { e.preventDefault(); editor?.chain().focus().undo().run(); }}
              title="Undo"
            >
              <Undo2 className="w-4 h-4" />
            </Button>
            <Button
              type="button"
              size="icon"
              variant="outline"
              onMouseDown={(e) => { e.preventDefault(); editor?.chain().focus().redo().run(); }}
              title="Redo"
            >
              <Redo2 className="w-4 h-4" />
            </Button>
            {/* NEW: Open in Chat / Project actions */}
            {chatId && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onMouseDown={(e) => {
                  e.preventDefault();
                  const url = `/chat/${chatId}`;
                  window.open(url, '_blank');
                }}
                title="Open this document's chat"
              >
                Open in Chat
              </Button>
            )}
            {projectId && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onMouseDown={(e) => {
                  e.preventDefault();
                  const url = `/project/${projectId}`;
                  window.open(url, '_blank');
                }}
                title="Open this document's project"
              >
                Open in Project
              </Button>
            )}
            {/* Review pane toggle (kept to open/close panel like Info pane) */}
            <Button
              type="button"
              size="sm"
              variant="outline"
              onMouseDown={(e) => {
                e.preventDefault();
                setShowReviewPane((v) => !v);
              }}
              title={showReviewPane ? 'Hide Review Pane' : 'Show Review Pane'}
            >
              {showReviewPane ? <SidebarClose className="w-3 h-3 mr-1" /> : <SidebarOpen className="w-3 h-3 mr-1" />}
              Review
            </Button>
            {/* Info pane toggle */}
            <Button
              type="button"
              size="sm"
              variant="outline"
              onMouseDown={(e) => {
                e.preventDefault();
                setShowInfoPane((v) => !v);
              }}
              title={showInfoPane ? 'Hide Info Pane' : 'Show Info Pane'}
            >
              {showInfoPane ? <SidebarClose className="w-3 h-3 mr-1" /> : <SidebarOpen className="w-3 h-3 mr-1" />}
              Info
            </Button>
            {/* NEW: Review Mode toggle */}
            <Button
              type="button"
              size="sm"
              variant={reviewMode ? 'secondary' : 'outline'}
              onMouseDown={(e) => {
                e.preventDefault();
                const next = !reviewMode;
                setReviewMode(next);
                if (!next) {
                  // optional: clear pending without committing on disable
                  pendingInsertRef.current = null;
                  if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); idleTimerRef.current = null; }
                  setShowReviewPane(false);
                } else {
                  // reset baseline when enabling to avoid mass mark
                  baselineTextRef.current = (editor?.getText() || '').trim();
                  hasInitializedRef.current = true;
                }
              }}
              title="Toggle Review Mode"
            >
              {reviewMode ? 'Review Mode: ON' : 'Review Mode: OFF'}
            </Button>
            {/* NEW: Completed/Reopen button */}
            <Button
              type="button"
              size="sm"
              variant={isLocked ? 'secondary' : 'outline'}
              onMouseDown={(e) => { 
                e.preventDefault(); 
                if (!isLocked) { 
                  handleCompletedClick(); 
                } else {
                  // Open project picker to link to more projects
                  setShowLinkDialog(true);
                }
              }}
              disabled={isLocked}
              title={isLocked ? 'Document is locked' : 'Lock document (add to Project)'}
              className="gap-1"
            >
              <Lock className="w-3 h-3" />
              {isLocked ? 'Locked' : 'Lock'}
            </Button>
          </div>
        </div>

        {/* Row 2: File actions */}
        <MapleWordHeaderActions
          isSaving={isSaving}
          isLoading={isLoading}
          onNew={handleNewDocument}
          onOpen={handleLoad}
          onSave={handleSave}
          onSaveAs={handleSaveAs}
          onExportHtml={handleExportHTML}
          onExportText={handleExportText}
          onExportDocx={handleExportDocx}
          onPrint={() => {
            const html = editor?.getHTML() || '';
            const printWindow = window.open('', '_blank');
            if (printWindow) {
              printWindow.document.write(`
                <html>
                  <head>
                    <title>${documentTitle}</title>
                    <style>
                      body { font-family: Arial, sans-serif; margin: 40px; line-height: 1.6; }
                      img { max-width: 100%; height: auto; }
                    </style>
                  </head>
                  <body>${html}</body>
                </html>
              `);
              printWindow.document.close();
              printWindow.print();
            }
          }}
        />

        {/* Ribbon toggle (now visible at all sizes) */}
        <MapleWordRibbonToggle
          collapsed={ribbonCollapsed}
          onToggle={() => setRibbonCollapsed((v) => !v)}
        />

        {/* Row 3: TipTap-style menubar */}
        {!ribbonCollapsed && <MapleWordMenuBar editor={editor} />}
      </div>

      {/* Editor + Panels */}
      <div className="flex-1 p-4 overflow-hidden" data-window-interactive="true">
        <Card className="h-full flex flex-col">
          <div
            className="flex-1 editor-shell relative overflow-hidden flex"
            data-window-interactive="true"
            onMouseDown={handleCanvasMouseDown}
          >
            {/* LEFT: Review Panel (renders like Info pane) */}
            {showReviewPane && (
              <MapleWordReviewPanel
                open={showReviewPane}
                onClose={() => setShowReviewPane(false)}
                changes={changes}
                onAccept={acceptChange}
                onReject={rejectChange}
                onAcceptAll={acceptAll}
                onRejectAll={rejectAll}
              />
            )}

            {/* Editor */}
            <div
              className="h-full border rounded relative bg-background editor-container overflow-auto flex-1 mr-4"
              data-window-interactive="true"
              onContextMenu={showEditorContextMenu}
            >
              {showCode ? (
                <textarea
                  className="w-full h-full p-3 bg-background text-foreground text-sm font-mono outline-none pb-56"
                  value={htmlContent}
                  onChange={(e) => {
                    setHtmlContent(e.target.value);
                    editor?.commands.setContent(e.target.value);
                  }}
                />
              ) : (
                <EditorContent
                  editor={editor}
                  className="prose prose-sm max-w-none min-h-full pl-6 pr-6 pb-56 text-foreground tiptap-content dark:prose-invert"
                />

              )}
            </div>

            {/* RIGHT: Info Pane */}
            {showInfoPane && (
              <MapleWordInfoPane
                open={showInfoPane}
                onClose={() => setShowInfoPane(false)}
                info={infoContext}
              />
            )}
          </div>

          {/* Status bar */}
          <MapleWordStatusBar
            editor={editor}
            wordsFallback={wordCount}
            charsFallback={characterCount}
            isSaving={isSaving}
            autoSaving={autoSaving}
            isSaved={isSaved}
          />
        </Card>
      </div>

      {/* File dialogs */}
      <DraggableFileDialog
        isOpen={showFileBrowser}
        onClose={() => setShowFileBrowser(false)}
        onFileSelect={handleFileSelect}
        mode="open"
        acceptedTypes={['application/x-mapleword', 'text/html', 'text/plain']}
        title="Open Document"
        initialPath={usernameReady ? getUserDocumentsPath() : 'C:\\User\\Default\\Documents'}
        homePath={usernameReady ? getUserDocumentsPath() : 'C:\\User\\Default\\Documents'}
      />

      <DraggableFileDialog
        isOpen={showSaveDialog}
        onClose={() => setShowSaveDialog(false)}
        mode="save"
        initialPath={usernameReady ? getUserDocumentsPath() : 'C:\\User\\Default\\Documents'}
        homePath={usernameReady ? getUserDocumentsPath() : 'C:\\User\\Default\\Documents'}
        title="Save Document"
        fileName={saveFileName}
        onFileNameChange={setSaveFileName}
        onSave={handleSaveLocation}
      />

      {/* Fixed AI bar overlay moved to root level for better z-index stacking */}
      <MapleWordAiMenu
        editor={editor}
        isAIStreamingRef={isAIStreamingRef}
        aiStreamStartPosRef={aiStreamStartPosRef}
        onAIStreamComplete={commitAIStreamedText}
      />

      {/* Context Menu */}
      <ContextMenu
        items={contextMenu.items}
        x={contextMenu.x}
        y={contextMenu.y}
        isVisible={contextMenu.isVisible}
        onClose={hideContextMenu}
      />

      {/* Link Project Dialog */}
      <MapleWordLinkProjectDialog
        open={showLinkDialog}
        onClose={() => setShowLinkDialog(false)}
        onLinked={async (pid) => {
          await linkDocumentToProject(pid);
        }}
      />
    </div>
  );
};

export default MapleWord;